import {
  DateCell,
  LoadingCell,
  RowObjectV2,
  TagCell,
  TagColor,
} from '@equip.health/ui';
import { first } from 'lodash';

import { NUMBER_OF_TABLE_LOADING_ROWS } from '~/lib/constants';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import { doesCurrentUserHaveRole } from '~/lib/util/user.util';

import AssignmentCell from './Cells/InquiryAssignmentCell';
import ContactCell from './Cells/InquiryContactCell';
import InquiryDateCell from './Cells/InquiryDateCell';
import InsuranceReviewStatusCell from './Cells/InquiryInsuranceReviewStatusCell';
import MedicalClearanceStatusCell from './Cells/InquiryMedicalClearanceStatusCell';
import PatientCell from './Cells/InquiryPatientCell';
import InquiryStatusCell from './Cells/InquiryStatusCell';
import { getIsPatientPrimaryContact } from '../InquiryDetail/viewUtil';
import {
  getAssigneesForRole,
  InquiryChannelTagColorMap,
  InquiryStatusTagDropdownOptions,
  InquiryTableColumn,
  InquiryTableColumnKeys,
} from './utils';
import PreliminaryDiagnosisCell from './Cells/InquiryPreliminaryDiagnoisisCell';
import MedicalAppointmentDateCell from './Cells/InquiryMedicalAppointmentDateCell';
import InquiryIntakeSurveyStatusCell from './Cells/InquiryIntakeSurveyStatusCell';
import InquiryTargetAdmissionDateCell from './Cells/InquiryTargetAdmissionDateCell';
import InquiryNextStepsCell from './Cells/InquiryNextStepsCell';
import InquiryInsuranceCompanyNameCell from './Cells/InquiryInsuranceCompanyNameCell';

const getLoadingRow = (id) => {
  return {
    admissionsOperationsRep: (
      <LoadingCell id={`admissions-operations-rep__${id}`} />
    ),
    admissionsSpecialist: <LoadingCell id={`admissions-specialist__${id}`} />,
    admissionsSupervisor: (
      <LoadingCell id={`admissions-supervisor__${id}`} />
    ),
    channel: <LoadingCell id={`channel__${id}`} />,
    channelType: <LoadingCell id={`channel-type__${id}`} />,
    createdDate: <LoadingCell id={`created-date__${id}`} />,
    inquiryNextSteps: <LoadingCell id={`inquiry-next-step__${id}`} />,
    insuranceCompanyName: <LoadingCell id={`insurance-company-name__${id}`} />,
    insuranceReviewStatus: <LoadingCell id={`insurance-status__${id}`} />,
    intakeSurveyStatus: <LoadingCell id={`intake-survey-status__${id}`} />,
    lastContactDate: <LoadingCell id={`last-contact-date__${id}`} />,
    medicalAppointmentDate: (
      <LoadingCell id={`medical-appointment-date__${id}`} />
    ),
    medicalClearanceStatus: (
      <LoadingCell id={`medical-clearance-status__${id}`} />
    ),
    modeOfInquiry: <LoadingCell id={`mode-of-inquiry__${id}`} />,
    nextContactDate: <LoadingCell id={`next-contact-date__${id}`} />,
    patientName: <LoadingCell id={`patient-name__${id}`} />,
    patientServiceRep: <LoadingCell id={`patient-service-rep__${id}`} />,
    patientState: <LoadingCell id={`patient-state__${id}`} />,
    preferredLanguage: <LoadingCell id={`preferred-language__${id}`} />,
    preliminaryDiagnosis: <LoadingCell id={`preliminary-diagnosis__${id}`} />,
    primaryContact: <LoadingCell id={`primary-contact__${id}`} />,
    qcApptDate: <LoadingCell id={`qc-appt-date__${id}`} />,
    referralCoordinator: <LoadingCell id={`referral-coordinator__${id}`} />,
    status: <LoadingCell id={`status__${id}`} />,
    targetAdmitDate: <LoadingCell id={`target-admit-date__${id}`} />,
    timezone: <LoadingCell id={`timezone__${id}`} />,
  };
};

export const InquiryTableRow = ({
  communicationOptions,
  coverageStatusOptions,
  currentUser,
  inquiry,
  isLoading,
  selectedInquiryId,
  diagnosisOptions,
  medicalNotClearedReasonOptions,
  medicalStatusOptions,
  medicalTypeOptions,
  handleUpdateMedicalReview,
  handleUpdateMedicalReviewStatus,
  handleAddAssignment,
  handleChangeTargetAdmissionDate,
  handleRemoveAssignment,
  handleSupervisorAssignment,
  updateNextSteps,
  updateInsuranceReviewStatus,
  updateNextContactDate,
  AddNote,
}: InquiryTableRowProps): RowObjectV2<InquiryTableColumn> => {
  const { inquiryId, version } = inquiry;

  const isPatientPrimaryContact = getIsPatientPrimaryContact(
    inquiry?.patient?.id,
    inquiry?.primaryContact?.externalId,
  );

  // Some patients have the string "null" stored as their state - this should
  // be treated as a null value
  const patientState = inquiry?.patient?.state;
  const displayPatientState =
    patientState && patientState !== 'null' ? patientState : undefined;

  const onSupervisorAddAS = handleSupervisorAssignment
    ? (
        provider: GetProviderByIdResponse,
        currentAssignee?: InquiryListItemAssignment,
      ) =>
        handleSupervisorAssignment(
          provider,
          inquiryId,
          CareTeamProviderType.ADMISSIONS_SPECIALIST,
          version,
          currentAssignee,
        )
    : undefined;

  const onSupervisorAddPSR = handleSupervisorAssignment
    ? (
        provider: GetProviderByIdResponse,
        currentAssignee?: InquiryListItemAssignment,
      ) =>
        handleSupervisorAssignment(
          provider,
          inquiryId,
          CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE,
          version,
          currentAssignee,
        )
    : undefined;

  return {
    isHighlighted: inquiryId === selectedInquiryId,
    key: inquiryId,
    object: isLoading
      ? getLoadingRow(inquiryId)
      : {
          admissionsOperationsRep: (
            <AssignmentCell
              doesCurrentUserMatchRole={doesCurrentUserHaveRole(
                currentUser,
                CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR,
              )}
              firstAssignee={first(
                getAssigneesForRole(
                  inquiry,
                  CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR,
                ),
              )}
              handleAddAssignment={handleAddAssignment}
              handleRemoveAssignment={handleRemoveAssignment}
              inquiryId={inquiryId}
              providerRole={
                CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR
              }
              version={version}
            />
          ),
          admissionsSpecialist: (
            <AssignmentCell
              doesCurrentUserMatchRole={doesCurrentUserHaveRole(
                currentUser,
                CareTeamProviderType.ADMISSIONS_SPECIALIST,
              )}
              firstAssignee={first(
                getAssigneesForRole(
                  inquiry,
                  CareTeamProviderType.ADMISSIONS_SPECIALIST,
                ),
              )}
              handleAddAssignment={handleAddAssignment}
              handleRemoveAssignment={handleRemoveAssignment}
              inquiryId={inquiryId}
              onSupervisorAdd={onSupervisorAddAS}
              providerRole={CareTeamProviderType.ADMISSIONS_SPECIALIST}
              version={version}
            />
          ),
          admissionsSupervisor: (
            <AssignmentCell
              doesCurrentUserMatchRole={doesCurrentUserHaveRole(
                currentUser,
                CareTeamProviderType.ADMISSIONS_SUPERVISOR,
              )}
              firstAssignee={first(
                getAssigneesForRole(
                  inquiry,
                  CareTeamProviderType.ADMISSIONS_SUPERVISOR,
                ),
              )}
              handleAddAssignment={handleAddAssignment}
              handleRemoveAssignment={handleRemoveAssignment}
              inquiryId={inquiryId}
              providerRole={CareTeamProviderType.ADMISSIONS_SUPERVISOR}
              version={version}
            />
          ),
          channel: (
            <TagCell
              color={
                inquiry?.channel
                  ? InquiryChannelTagColorMap[inquiry.channel]
                  : TagColor.marigold
              }
              id={`channel__inquiry-${inquiryId}`}
              value={inquiry?.channel}
            />
          ),
          channelType: inquiry?.channelType,
          createdDate: inquiry?.inquiryDate ? (
            <DateCell
              date={inquiry?.inquiryDate ? new Date(inquiry.inquiryDate) : null}
              id={`created__inquiry-${inquiryId}`}
            />
          ) : null,
          inquiryNextSteps: (
            <InquiryNextStepsCell
              inquiryId={inquiryId}
              nextSteps={inquiry?.nextSteps}
              onSave={updateNextSteps}
              version={version}
            />
          ),
          insuranceCompanyName: (
            <InquiryInsuranceCompanyNameCell
              id={`insurance-company-name__${inquiryId}`}
              payor={inquiry?.payor}
            />
          ),
          insuranceReviewStatus: (
            <InsuranceReviewStatusCell
              coverageStatus={
                inquiry?.coverageReview?.description ?? 'Not ready'
              }
              coverageStatusOptions={coverageStatusOptions}
              id={`insurance-review-status-${inquiryId}`}
              inquiryId={inquiryId}
              updateInsuranceReviewStatus={updateInsuranceReviewStatus}
              version={version}
            />
          ),
          intakeSurveyStatus: (
            <InquiryIntakeSurveyStatusCell
              inquiryId={inquiryId}
              intakeSurveyStatus={inquiry?.intakeSurveyStatus}
            />
          ),
          lastContactDate: (
            <InquiryDateCell
              AddNote={AddNote}
              cellType={InquiryTableColumnKeys.lastContactDate}
              communicationOptions={communicationOptions}
              dateString={inquiry?.lastContactDate}
              inquiryId={inquiryId}
              isPatientPrimaryContact={isPatientPrimaryContact}
              patientId={inquiry?.patient?.id}
              updateNextContactDate={updateNextContactDate}
              version={version}
            />
          ),
          medicalAppointmentDate: (
            <MedicalAppointmentDateCell
              appointmentDate={inquiry?.medicalAppointmentDate}
              id={`medical-appointment-date__inquiry-${inquiryId}`}
            />
          ),
          medicalClearanceStatus: (
            <MedicalClearanceStatusCell
              diagnosisOptions={diagnosisOptions}
              id={`medical-clearance-status-${inquiryId}`}
              inquiryId={inquiryId}
              medicalNotClearedReasonOptions={medicalNotClearedReasonOptions}
              medicalReview={inquiry?.medicalReview}
              medicalStatusOptions={medicalStatusOptions}
              medicalTypeOptions={medicalTypeOptions}
              patientId={inquiry?.patient?.id}
              updateMedicalReview={handleUpdateMedicalReview}
              updateMedicalReviewStatus={handleUpdateMedicalReviewStatus}
              version={version}
            />
          ),
          modeOfInquiry: inquiry?.informationSource,
          nextContactDate: (
            <InquiryDateCell
              AddNote={AddNote}
              cellType={InquiryTableColumnKeys.nextContactDate}
              communicationOptions={communicationOptions}
              dateString={inquiry?.nextContactDate}
              inquiryId={inquiryId}
              isPatientPrimaryContact={isPatientPrimaryContact}
              patientId={inquiry?.patient?.id}
              updateNextContactDate={updateNextContactDate}
              version={version}
            />
          ),
          patientName: (
            <PatientCell inquiryId={inquiryId} patient={inquiry?.patient} />
          ),
          patientServiceRep: (
            <AssignmentCell
              doesCurrentUserMatchRole={doesCurrentUserHaveRole(
                currentUser,
                CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE,
              )}
              firstAssignee={first(
                getAssigneesForRole(
                  inquiry,
                  CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE,
                ),
              )}
              handleAddAssignment={handleAddAssignment}
              handleRemoveAssignment={handleRemoveAssignment}
              inquiryId={inquiryId}
              onSupervisorAdd={onSupervisorAddPSR}
              providerRole={CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE}
              version={version}
            />
          ),
          patientState: displayPatientState,
          preferredLanguage: inquiry.preferredLanguage,
          preliminaryDiagnosis: (
            <PreliminaryDiagnosisCell
              inquiryId={inquiryId}
              preliminaryDiagnosis={inquiry?.diagnosisShortDescription}
            />
          ),
          primaryContact: (
            <ContactCell
              inquiryId={inquiryId}
              primaryContact={inquiry?.primaryContact}
            />
          ),
          qcApptDate: (
            <DateCell
              date={
                inquiry?.qualifyingCallDate
                  ? new Date(inquiry.qualifyingCallDate)
                  : null
              }
              id={`qc-appt-date__inquiry-${inquiryId}`}
              shouldShowRelativeTime
            />
          ),
          referralCoordinator: (
            <AssignmentCell
              doesCurrentUserMatchRole={doesCurrentUserHaveRole(
                currentUser,
                CareTeamProviderType.PROVIDER_PARTNERSHIPS,
              )}
              firstAssignee={first(
                getAssigneesForRole(
                  inquiry,
                  CareTeamProviderType.PROVIDER_PARTNERSHIPS,
                ),
              )}
              handleAddAssignment={handleAddAssignment}
              handleRemoveAssignment={handleRemoveAssignment}
              inquiryId={inquiryId}
              providerRole={CareTeamProviderType.PROVIDER_PARTNERSHIPS}
              version={version}
            />
          ),
          status:
            inquiry?.inquiryStatus === InquiryStatusValue.ENROLLED ? (
              <TagCell
                color={
                  InquiryStatusTagDropdownOptions.find(
                    (s) => s.value === InquiryStatusValue.ENROLLED,
                  )?.color
                }
                id={`inquiry-status__${inquiryId}`}
                value={inquiry?.inquiryStatus}
              />
            ) : (
              <InquiryStatusCell
                inquiryId={inquiryId}
                inquiryStatus={inquiry?.inquiryStatus}
                targetTreatmentProgram={inquiry?.targetTreatmentProgram}
                version={version}
                patientId={inquiry?.patient?.id}
              />
            ),
          targetAdmitDate: (
            <InquiryTargetAdmissionDateCell
              handleChange={handleChangeTargetAdmissionDate}
              id={`target-admission-date-${inquiryId}`}
              inquiryId={inquiryId}
              targetAdmissionDate={inquiry?.targetAdmissionDate}
              version={version}
            />
          ),
          targetTreatmentProgram: inquiry?.targetTreatmentProgram,
          timezone: inquiry?.timezone ?? inquiry?.patient?.timezone,
        },
  };
};

export const loadingRows: RowObjectV2<InquiryTableColumn>[] = Array.from(
  { length: NUMBER_OF_TABLE_LOADING_ROWS },
  (_, i) => ({
    key: `loading-${i}`,
    object: getLoadingRow(`loading-${i}`),
  }),
);
